import apiClient from './client';
import { apiRequestError } from './errors';
import type { User, ApiResponse } from '../types';

export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<ApiResponse<User>>('/api/v1/auth/me');
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get user');
  }
  return response.data.data;
}

export async function updateProfile(data: { name?: string }): Promise<User> {
  const response = await apiClient.patch<ApiResponse<User>>(
    '/api/v1/auth/me',
    data
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to update profile');
  }
  return response.data.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/api/v1/auth/logout');
}

export async function requestEmailChange(newEmail: string): Promise<{ expires_at: string }> {
  try {
    const response = await apiClient.post<ApiResponse<{ expires_at: string }>>(
      '/api/v1/auth/email-change/request',
      { new_email: newEmail }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to request email change');
    }
    return response.data.data;
  } catch (error) {
    // Preserve the error slug (e.g. 'email_in_use') so the UI can branch on it
    throw apiRequestError(error, 'Failed to request email change');
  }
}

export async function confirmEmailChange(code: string): Promise<{ email: string }> {
  try {
    const response = await apiClient.post<ApiResponse<{ email: string }>>(
      '/api/v1/auth/email-change/confirm',
      { code }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to confirm email change');
    }
    return response.data.data;
  } catch (error) {
    // Preserve the error slug (e.g. 'invalid_code') and attemptsRemaining must
    // survive the wrapping, so the UI can show "N attempts remaining"
    throw apiRequestError(error, 'Failed to confirm email change');
  }
}

/**
 * Tell portal-api the user just changed (or, passkey-only, added) their password in
 * Settings, through their own Hanko session. portal-api writes the audit row and emails
 * a notice. It never touches Hanko, so callers treat a failure as non-fatal.
 */
export async function reportPasswordChanged(
  kind: 'updated' | 'created'
): Promise<{ notice_sent: boolean }> {
  try {
    const response = await apiClient.post<ApiResponse<{ notice_sent: boolean }>>(
      '/api/v1/auth/password-changed',
      { kind }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to record the password change');
    }
    return response.data.data;
  } catch (error) {
    throw apiRequestError(error, 'Failed to record the password change');
  }
}
